import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountDto,
  AccountLinkDto,
  Comparison,
  CreateAccountRequest,
  CreatedAccountDto,
  PlatformSettingsDto,
  RolePermissionsDto,
  ComparisonList,
  ConfigurationReleaseDto,
  Dashboard,
  DecideRecommendation,
  DismissGuidance,
  FeedbackSummary,
  FieldMetrics,
  Funnel,
  GuidanceDecisionResult,
  GuidanceHistoryEntry,
  JourneyGraph,
  JourneySummary,
  ProjectDto,
  RecommendationDecisionResult,
  RecommendationDto,
  RecommendationFilters,
  RecommendationPage,
  ScriptStatusDto,
  UpdateGuidance,
} from '@ederest/contracts';
import { api } from './client.js';

export function useProjects() {
  return useQuery({ queryKey: ['projects'], queryFn: () => api<ProjectDto[]>('/composition/projects') });
}

/** `queryString` is the serialised filter set (functional §5.4); it is part of the cache key. */
export function useDashboard(projectId: string | undefined, queryString = '') {
  return useQuery({
    queryKey: ['dashboard', projectId, queryString],
    enabled: Boolean(projectId),
    queryFn: () => api<Dashboard>(`/analytics/projects/${projectId}/dashboard?${queryString}`),
  });
}

export function useFunnel(projectId: string | undefined, journeyId: string | undefined, queryString = '') {
  return useQuery({
    queryKey: ['funnel', projectId, journeyId, queryString],
    enabled: Boolean(projectId && journeyId),
    queryFn: () =>
      api<Funnel>(`/analytics/projects/${projectId}/journeys/${journeyId}/funnel?${queryString}`),
  });
}

export function useFieldMetrics(projectId: string | undefined, queryString = '') {
  return useQuery({
    queryKey: ['field-metrics', projectId, queryString],
    enabled: Boolean(projectId),
    queryFn: () => api<FieldMetrics>(`/analytics/projects/${projectId}/field-metrics?${queryString}`),
  });
}

/** The voice of the user by entity, announcement and type (functional §5.8), under the same filters. */
export function useFeedbackSummary(projectId: string | undefined, queryString = '') {
  return useQuery({
    queryKey: ['feedback', projectId, queryString],
    enabled: Boolean(projectId),
    // A filter change keeps the previous figures on screen until the new ones arrive.
    placeholderData: keepPreviousData,
    queryFn: () => api<FeedbackSummary>(`/analytics/projects/${projectId}/feedback?${queryString}`),
  });
}

/** The published versions a before/after comparison exists for (functional §5.4 rule 4), newest first. */
export function useComparisons(projectId: string | undefined) {
  return useQuery({
    queryKey: ['comparisons', projectId],
    enabled: Boolean(projectId),
    queryFn: () => api<ComparisonList>(`/analytics/projects/${projectId}/comparisons`),
  });
}

/** The before/after comparison of one version; the criteria (journey, language, device) are part of the key. */
export function useComparison(
  projectId: string | undefined,
  releaseVersion: number | undefined,
  queryString = '',
) {
  return useQuery({
    queryKey: ['comparison', projectId, releaseVersion, queryString],
    enabled: Boolean(projectId && releaseVersion),
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: () =>
      api<Comparison>(`/analytics/projects/${projectId}/comparisons/${releaseVersion}?${queryString}`),
  });
}

export function useJourneys(projectId: string | undefined) {
  return useQuery({
    queryKey: ['journeys', projectId],
    enabled: Boolean(projectId),
    queryFn: () => api<JourneySummary[]>(`/composition/projects/${projectId}/journeys`),
  });
}

export function useJourneyGraph(journeyId: string | undefined) {
  return useQuery({
    queryKey: ['journey', journeyId],
    enabled: Boolean(journeyId),
    queryFn: () => api<JourneyGraph>(`/composition/journeys/${journeyId}`),
  });
}

/** The row version the screen displayed travels as `If-Match` (technical §13.8). */
function ifMatch(rowVersion: number): HeadersInit {
  return { 'if-match': `"${rowVersion}"` };
}

interface Versioned {
  id: string;
  rowVersion: number;
}

/**
 * Guidance decisions (functional §8.4): the product's only editing surface,
 * shared by the journey tab and the recommendation queue. Every decision
 * invalidates the journeys, the queue and the change log; a stale row version
 * answers 412 and the screen asks to reload.
 */
export function useGuidanceActions() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    for (const key of [
      'journey',
      'journeys',
      'recommendations',
      'recommendation',
      'guidance-history',
      'releases',
    ]) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
  const decision = (path: (id: string) => string, method: 'POST' | 'DELETE') =>
    useMutation({
      mutationFn: ({ id, rowVersion }: Versioned) =>
        api<GuidanceDecisionResult>(path(id), { method, headers: ifMatch(rowVersion) }),
      onSuccess: invalidate,
    });

  return {
    approve: decision((id) => `/composition/guidances/${id}/approval`, 'POST'),
    revert: decision((id) => `/composition/guidances/${id}/approval`, 'DELETE'),
    publish: decision((id) => `/composition/guidances/${id}/publication`, 'POST'),
    disable: decision((id) => `/composition/guidances/${id}/publication`, 'DELETE'),
    remove: decision((id) => `/composition/guidances/${id}`, 'DELETE'),
    dismiss: useMutation({
      mutationFn: ({ id, rowVersion, ...body }: Versioned & DismissGuidance) =>
        api<GuidanceDecisionResult>(`/composition/guidances/${id}/dismissal`, {
          method: 'POST',
          headers: ifMatch(rowVersion),
          body: JSON.stringify(body),
        }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, rowVersion, patch }: Versioned & { patch: UpdateGuidance }) =>
        api<GuidanceDecisionResult>(`/composition/guidances/${id}`, {
          method: 'PATCH',
          headers: ifMatch(rowVersion),
          body: JSON.stringify(patch),
        }),
      onSuccess: invalidate,
    }),
  };
}
export type GuidanceActions = ReturnType<typeof useGuidanceActions>;

/** The change log of one guidance (functional §8.4), oldest first. */
export function useGuidanceHistory(guidanceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['guidance-history', guidanceId],
    enabled: Boolean(guidanceId) && enabled,
    queryFn: () => api<GuidanceHistoryEntry[]>(`/composition/guidances/${guidanceId}/history`),
  });
}

/** The validation queue (functional §5.6), filtered and paginated. */
export function useRecommendations(projectId: string | undefined, filters: Partial<RecommendationFilters>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const queryString = query.toString();
  return useQuery({
    queryKey: ['recommendations', projectId, queryString],
    enabled: Boolean(projectId),
    placeholderData: keepPreviousData,
    queryFn: () =>
      api<RecommendationPage>(`/composition/projects/${projectId}/recommendations?${queryString}`),
  });
}

export function useRecommendation(id: string | undefined) {
  return useQuery({
    queryKey: ['recommendation', id],
    enabled: Boolean(id),
    queryFn: () => api<RecommendationDto>(`/composition/recommendations/${id}`),
  });
}

/** One decision per recommendation (technical §11.2); a platform recommendation is decided here alone. */
export function useRecommendationDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & DecideRecommendation) =>
      api<RecommendationDecisionResult>(`/composition/recommendations/${id}/decision`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      for (const key of ['recommendations', 'recommendation', 'journey', 'journeys', 'guidance-history']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

export function useJourneyActions(journeyId: string | undefined) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['journey', journeyId] });
    void queryClient.invalidateQueries({ queryKey: ['journeys'] });
  };

  return {
    rename: useMutation({
      mutationFn: (name: string) =>
        api<void>(`/composition/journeys/${journeyId}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
      onSuccess: invalidate,
    }),
    setTracked: useMutation({
      mutationFn: (tracked: boolean) =>
        api<void>(`/composition/journeys/${journeyId}`, {
          method: 'PATCH',
          body: JSON.stringify({ tracked }),
        }),
      onSuccess: invalidate,
    }),
    renameStep: useMutation({
      mutationFn: ({ stepId, name }: { stepId: string; name: string }) =>
        api<void>(`/composition/journeys/${journeyId}/steps/${stepId}`, {
          method: 'PATCH',
          body: JSON.stringify({ name }),
        }),
      onSuccess: invalidate,
    }),
    groupSteps: useMutation({
      mutationFn: (stepIds: string[]) =>
        api<void>(`/composition/journeys/${journeyId}/step-groups`, {
          method: 'POST',
          body: JSON.stringify({ stepIds }),
        }),
      onSuccess: invalidate,
    }),
  };
}

/** Script administration (module M1): tag, switch, versions. */
export function useScriptStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ['script', projectId],
    enabled: Boolean(projectId),
    queryFn: () => api<ScriptStatusDto>(`/composition/projects/${projectId}/script`),
  });
}

export function useReleases(projectId: string | undefined) {
  return useQuery({
    queryKey: ['releases', projectId],
    enabled: Boolean(projectId),
    queryFn: () => api<ConfigurationReleaseDto[]>(`/composition/projects/${projectId}/releases`),
  });
}

export function useScriptActions(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['script', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['releases', projectId] });
  };

  return {
    suspend: useMutation({
      mutationFn: () => api<void>(`/composition/projects/${projectId}/script/suspension`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
    resume: useMutation({
      mutationFn: () =>
        api<void>(`/composition/projects/${projectId}/script/suspension`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    activate: useMutation({
      mutationFn: (version: number) =>
        api<void>(`/composition/projects/${projectId}/releases/${version}/activation`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
    publish: useMutation({
      mutationFn: () =>
        api<{ version: number }>(`/composition/projects/${projectId}/releases`, { method: 'POST' }),
      onSuccess: invalidate,
    }),
  };
}

/** Accounts of the platform (module M14), central access only. */
export function useAccounts() {
  return useQuery({ queryKey: ['accounts'], queryFn: () => api<AccountDto[]>('/administration/accounts') });
}

export function useAccountActions() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['accounts'] });
  const path = (id: string) => `/administration/accounts/${id}`;
  return {
    create: useMutation({
      mutationFn: (body: CreateAccountRequest) =>
        api<CreatedAccountDto>('/administration/accounts', { method: 'POST', body: JSON.stringify(body) }),
      onSuccess: refresh,
    }),
    changeScope: useMutation({
      mutationFn: ({ id, projectScope }: { id: string; projectScope: string[] }) =>
        api<AccountDto>(`${path(id)}/scope`, { method: 'PUT', body: JSON.stringify({ projectScope }) }),
      onSuccess: refresh,
    }),
    setSuspension: useMutation({
      mutationFn: ({ id, suspended }: { id: string; suspended: boolean }) =>
        api<AccountDto>(`${path(id)}/suspension`, { method: suspended ? 'PUT' : 'DELETE' }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<AccountDto>(path(id), { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    issueLink: useMutation({
      mutationFn: (id: string) => api<AccountLinkDto>(`${path(id)}/links`, { method: 'POST' }),
    }),
  };
}

export function usePlatformSettings() {
  return useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => api<PlatformSettingsDto>('/administration/platform-settings'),
  });
}

export function useUpdatePlatformSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PlatformSettingsDto) =>
      api<PlatformSettingsDto>('/administration/platform-settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-settings'] }),
  });
}

/** Who may do what, as the API applies it (functional §9.3); read-only. */
export function useRolePermissions() {
  return useQuery({
    queryKey: ['role-permissions'],
    staleTime: Infinity,
    queryFn: () => api<RolePermissionsDto>('/administration/role-permissions'),
  });
}
